import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, setToken } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { LayoutDashboard, Loader2, LogOut, Search, ChevronRight, Bell, MailWarning, AlertCircle, FileDown, Edit3 } from "lucide-react";
import { STATUS_LABEL, FACTURACION_LABEL, BRAND, TIPO_UBICACION_LABEL, isActiveStand } from "@/lib/constants";
import { ServiceRequestsPanel } from "@/components/admin/ServiceRequestsPanel";
import { RentalCatalogManager } from "@/components/admin/RentalCatalogManager";
import UsersAndAccessPanel from "@/components/admin/UsersAndAccessPanel";
import SupplierOrdersPanel from "@/components/admin/SupplierOrdersPanel";

const inputCls = "bg-zinc-950 border-zinc-800 focus:border-[#FF5A00] focus-visible:ring-[#FF5A00]/30 rounded-sm";

const STATUS_BADGE = {
  enviado_para_revisar: "bg-amber-500/10 text-amber-300 border border-amber-500/40",
  aprobado: "bg-emerald-500/10 text-emerald-300 border border-emerald-500/40",
  rechazado: "bg-red-500/10 text-red-300 border border-red-500/40",
  default: "bg-zinc-800 text-zinc-500 border border-zinc-700",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);
  const [stands, setStands] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [sreqs, setSreqs] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/auth/me"),
      api.get("/admin/dashboard/stats"),
      api.get("/admin/stands"),
      api.get("/admin/notifications"),
      api.get("/admin/service-requests"),
    ]).then(([u, s, l, n, sr]) => {
      setMe(u.data); setStats(s.data); setStands(l.data); setNotifs(n.data); setSreqs(sr.data);
    }).catch(() => navigate("/admin")).finally(() => setLoading(false));
  }, [navigate]);

  const reloadSreqs = () => {
    api.get("/admin/service-requests").then(({ data }) => setSreqs(data));
    api.get("/admin/dashboard/stats").then(({ data }) => setStats(data));
  };

  const exportExcel = async () => {
    try {
      const res = await api.get("/admin/export/pedidos.xlsx", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "pedidos_full_equipado.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Listado exportado");
    } catch { toast.error("No se pudo exportar el listado"); }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.warn("logout backend call failed", e); }
    sessionStorage.removeItem("am_admin_edit_mode");
    sessionStorage.removeItem("mis_espacios");
    window.dispatchEvent(new Event("am-admin-edit-changed"));
    setToken("");
    navigate("/");
  };

  if (loading) return <div className="min-h-screen bg-[#09090B] flex items-center justify-center text-zinc-400"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando...</div>;

  const filteredAll = stands.filter((s) =>
    !s.merged_into && (
      !search ||
      s.company.toLowerCase().includes(search.toLowerCase()) ||
      s.stand_number.toLowerCase().includes(search.toLowerCase())
    )
  );
  const activeStands = filteredAll.filter((s) => isActiveStand(s.status));
  const inactiveStands = filteredAll.filter((s) => !isActiveStand(s.status));
  const needsReview = stands.filter((s) => s.requires_review);
  const emailPending = stands.filter((s) => s.email_pending);

  const enterAdminEditMode = async (s) => {
    try {
      const { data } = await api.post(`/admin/stands/${s.id}/admin-edit-enter`);
      sessionStorage.setItem("am_admin_edit_mode", JSON.stringify({
        standId: s.id,
        standNumber: data.stand_number,
        primaryStand: data.primary_stand,
        adminEmail: me?.email,
        adminName: me?.name || me?.email,
        entryAt: new Date().toISOString(),
        company: data.company || s.company || "",
        status: data.status,
        requiresReason: data.requires_reason,
        warning: data.warning,
        backTo: "/admin/dashboard",
      }));
      sessionStorage.removeItem("mis_espacios");
      window.dispatchEvent(new Event("am-admin-edit-changed"));
      toast.success(`Modo Administrador · ${data.stand_number}`);
      navigate(data.target_url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo abrir el modo edición");
    }
  };

  const renderRow = (s) => {
    const up = s.configuration?.upgrades || {};
    const upSub = (up.items || []).reduce((x, c) => x + c.unit_price * c.quantity, 0);
    const tvxSub = (up.tv_extras || []).reduce((x, t) => x + (t.price || 0) * (t.quantity || 0), 0);
    const total = (s.base_price || 0) + upSub + tvxSub;
    const badgeCls = STATUS_BADGE[s.status] || STATUS_BADGE.default;
    return (
      <tr key={s.id} data-testid={`stand-row-${s.stand_number}`} className="border-t border-zinc-800 hover:bg-zinc-900/50 transition">
        <td className="p-3">
          <div className="font-medium flex items-center gap-2">
            {s.company || <span className="text-zinc-600 italic">Sin asignar</span>}
            {s.requires_review && <span title="Requiere revisión" className="w-2 h-2 rounded-full bg-[#FF5A00] inline-block" />}
            {s.email_pending && <MailWarning className="w-3.5 h-3.5 text-amber-400" title="Correo pendiente de reenvío" />}
          </div>
        </td>
        <td className="p-3 font-mono text-xs">
          {s.combo_members?.length > 1 ? s.combo_members.join("+") : s.stand_number}
          {s.combo_members?.length > 1 && <Badge className="ml-2 bg-[#FF5A00]/10 text-[#FF5A00] border border-[#FF5A00]/30 rounded-sm text-[9px]">UNIÓN ×{s.combo_members.length}</Badge>}
          {s.is_special && <Badge className="ml-2 bg-amber-500/10 text-amber-300 border border-amber-500/30 rounded-sm text-[9px]">Especial</Badge>}
        </td>
        <td className="p-3 font-mono text-xs text-zinc-400">{s.ancho}×{s.profundidad} m · {s.superficie} m²</td>
        <td className="p-3 text-xs text-zinc-400">{TIPO_UBICACION_LABEL[s.tipo_ubicacion] || "—"}</td>
        <td className="p-3">
          <span className={`inline-block px-2 py-1 rounded-sm text-[10px] font-semibold tracking-wider ${badgeCls}`}>{STATUS_LABEL[s.status] || s.status}</span>
        </td>
        <td className="p-3">
          {s.status === "aprobado" && s.facturacion_status ? (
            <span className="inline-block px-2 py-1 rounded-sm text-[10px] font-semibold tracking-wider bg-sky-500/10 text-sky-300 border border-sky-500/40">{FACTURACION_LABEL[s.facturacion_status]}</span>
          ) : <span className="text-zinc-700 text-xs">—</span>}
        </td>
        <td className="p-3 text-right font-mono text-[#FF5A00]">${Math.round(total).toLocaleString("es-AR")}</td>
        <td className="p-3">
          <div className="flex items-center gap-1 justify-end">
            <Button
              data-testid={`modificar-stand-${s.stand_number}`}
              size="sm"
              variant="outline"
              onClick={() => enterAdminEditMode(s)}
              className="border-[#FF5A00]/40 text-[#FF5A00] hover:bg-[#FF5A00]/10 rounded-sm h-8 text-[10px] font-semibold tracking-wider uppercase px-2"
              title="Ingresar en modo administrador para modificar/completar como expositor"
            >
              <Edit3 className="w-3 h-3 mr-1" /> Modificar
            </Button>
            <Link to={`/admin/stand/${s.id}`} data-testid={`open-stand-${s.stand_number}`}>
              <Button size="sm" variant="ghost" className="hover:bg-zinc-800 rounded-sm h-8" title="Ver detalle">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </td>
      </tr>
    );
  };

  const tableHead = (
    <thead className="bg-zinc-950 text-[10px] uppercase tracking-wider text-zinc-500">
      <tr>
        <th className="text-left p-3">Empresa</th>
        <th className="text-left p-3">N° Stand</th>
        <th className="text-left p-3">Medida</th>
        <th className="text-left p-3">Ubicación</th>
        <th className="text-left p-3">Estado del pedido</th>
        <th className="text-left p-3">Facturación y pago</th>
        <th className="text-right p-3">Total USD</th>
        <th className="p-3"></th>
      </tr>
    </thead>
  );

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <header className="border-b border-zinc-900 sticky top-0 z-10 backdrop-blur-xl bg-zinc-950/80">
        <div className="max-w-7xl mx-auto px-6 py-3.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src={BRAND.LOGO_AM} alt="AM Norte" className="h-9" />
            <div className="h-7 w-px bg-zinc-800" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Paquete Full Equipado</div>
              <div className="text-sm font-semibold flex items-center gap-2"><LayoutDashboard className="w-3.5 h-3.5 text-[#FF5A00]" /> Panel administrador</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <img src={BRAND.LOGO_SO} alt="Servicios Oficiales" className="hidden md:block h-7 invert opacity-70" />
            <div className="hidden md:flex items-center gap-2 text-xs text-zinc-400">
              {me?.picture && <img src={me.picture} alt="" className="w-7 h-7 rounded-full" />}
              <span>{me?.email}</span>
            </div>
            <Button data-testid="logout-btn" onClick={logout} variant="outline" className="border-zinc-800 rounded-sm h-9 text-xs hover:bg-zinc-900">
              <LogOut className="w-3.5 h-3.5 mr-1.5" /> Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {(needsReview.length > 0 || emailPending.length > 0) && (
          <div data-testid="attention-panel" className="mb-8 border border-[#FF5A00]/40 bg-[#FF5A00]/5 rounded-sm p-4">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#FF5A00] mb-3 flex items-center gap-2"><AlertCircle className="w-3.5 h-3.5" /> Atención pendiente</div>
            <div className="space-y-1.5">
              {needsReview.map((s) => (
                <Link key={s.id} to={`/admin/stand/${s.id}`} data-testid={`review-alert-${s.stand_number}`} className="flex items-center justify-between text-sm text-zinc-200 hover:text-[#FF5A00] transition py-1 border-b border-zinc-800/60 last:border-0">
                  <span>Nuevo pedido de FULL EQUIPADO para el stand <b>{s.stand_number}</b>{s.company ? ` · ${s.company}` : ""}.</span>
                  <ChevronRight className="w-4 h-4 shrink-0" />
                </Link>
              ))}
              {emailPending.map((s) => (
                <Link key={`ep-${s.id}`} to={`/admin/stand/${s.id}`} data-testid={`email-pending-alert-${s.stand_number}`} className="flex items-center justify-between text-sm text-amber-300 hover:text-amber-200 transition py-1 border-b border-zinc-800/60 last:border-0">
                  <span className="flex items-center gap-2"><MailWarning className="w-3.5 h-3.5" /> Correo pendiente de reenvío · Stand <b>{s.stand_number}</b></span>
                  <ChevronRight className="w-4 h-4 shrink-0" />
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-8">
          <Stat label="Total espacios" value={stats?.total} testid="stat-total" />
          <Stat label="Enviados para revisar" value={stats?.enviados_revisar} accent testid="stat-enviados" />
          <Stat label="Aprobados" value={stats?.aprobados} tone="emerald" testid="stat-aprobados" />
          <Stat label="Rechazados" value={stats?.rechazados} tone="red" testid="stat-rechazados" />
          <Stat label="Sin completar" value={stats?.sin_completar} testid="stat-sincompletar" />
          <Stat label="Falta facturar" value={stats?.falta_facturar} tone="sky" testid="stat-faltafacturar" />
          <Stat label="Factura enviada" value={stats?.factura_enviada} tone="sky" testid="stat-facturaenviada" />
          <Stat label="Pagados" value={stats?.pagados} tone="emerald" testid="stat-pagados" />
          <Stat label="Correos pendientes" value={stats?.correos_pendientes} tone="amber" testid="stat-correos" />
          <Stat label="Proyectos especiales" value={stats?.proyectos_especiales} tone="violet" testid="stat-proyectos" />
          <Stat label="Alquileres" value={stats?.alquileres} tone="teal" testid="stat-alquileres" />
        </div>

        <Tabs defaultValue="stands">
          <TabsList className="bg-zinc-900 border border-zinc-800 rounded-sm">
            <TabsTrigger value="stands" data-testid="tab-stands" className="data-[state=active]:bg-[#FF5A00] data-[state=active]:text-white rounded-sm">Espacios · Full Equipado</TabsTrigger>
            <TabsTrigger value="servicios" data-testid="tab-servicios" className="data-[state=active]:bg-[#FF5A00] data-[state=active]:text-white rounded-sm">
              Servicios Oficiales{sreqs.filter((r) => r.status === "enviado_para_revisar").length > 0 && <span className="ml-1.5 px-1.5 rounded-full bg-amber-500/20 text-amber-300 text-[10px]">{sreqs.filter((r) => r.status === "enviado_para_revisar").length}</span>}
            </TabsTrigger>
            <TabsTrigger value="catalogo" data-testid="tab-catalogo" className="data-[state=active]:bg-[#FF5A00] data-[state=active]:text-white rounded-sm">Catálogo de alquiler</TabsTrigger>
            <TabsTrigger value="proveedores" data-testid="tab-proveedores" className="data-[state=active]:bg-[#FF5A00] data-[state=active]:text-white rounded-sm">Pedidos a proveedores</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users" className="data-[state=active]:bg-[#FF5A00] data-[state=active]:text-white rounded-sm">Usuarios y accesos</TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-activity" className="data-[state=active]:bg-[#FF5A00] data-[state=active]:text-white rounded-sm">Actividad</TabsTrigger>
          </TabsList>

          <TabsContent value="stands" className="mt-4">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="relative flex-1 max-w-md">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input data-testid="search-stands" placeholder="Buscar por empresa o número de stand..." value={search} onChange={(e) => setSearch(e.target.value)} className={`${inputCls} pl-9 h-10`} />
              </div>
              <Button data-testid="export-excel-btn" onClick={exportExcel} variant="outline" className="border-zinc-700 hover:bg-zinc-800 rounded-sm h-10 text-xs">
                <FileDown className="w-3.5 h-3.5 mr-1.5" /> Exportar Excel
              </Button>
              <div className="text-xs text-zinc-500">
                {stands.length} stands precargados
              </div>
            </div>

            <Card className="bg-zinc-900 border-zinc-800 rounded-sm overflow-hidden">
              <div className="p-3 border-b border-zinc-800 text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex justify-between items-center">
                <span>Stands con pedido · {activeStands.length}</span>
                <span className="text-zinc-600 normal-case tracking-normal">Enviados para revisar, aprobados o rechazados</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  {tableHead}
                  <tbody>
                    {activeStands.map(renderRow)}
                    {activeStands.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-zinc-500 text-sm">Sin pedidos por ahora.</td></tr>}
                  </tbody>
                </table>
              </div>
            </Card>

            <div className="mt-6">
              <button
                data-testid="toggle-inactive-btn"
                onClick={() => setShowInactive(!showInactive)}
                className="w-full text-left text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-zinc-300 flex items-center justify-between p-3 border border-zinc-800 rounded-sm bg-zinc-950"
              >
                <span>Sin completar · {inactiveStands.length}</span>
                <span>{showInactive ? "Ocultar" : "Mostrar"}</span>
              </button>
              {showInactive && (
                <Card className="bg-zinc-900 border-zinc-800 rounded-sm overflow-hidden mt-2">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      {tableHead}
                      <tbody>{inactiveStands.map(renderRow)}</tbody>
                    </table>
                  </div>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="servicios" className="mt-4">
            <ServiceRequestsPanel requests={sreqs} reload={reloadSreqs} />
          </TabsContent>

          <TabsContent value="catalogo" className="mt-4">
            <RentalCatalogManager />
          </TabsContent>

          <TabsContent value="proveedores" className="mt-4">
            <SupplierOrdersPanel />
          </TabsContent>

          <TabsContent value="users" className="mt-4">
            <UsersAndAccessPanel />
          </TabsContent>

          <TabsContent value="activity" className="mt-4">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3"><Bell className="w-3.5 h-3.5" /> Actividad reciente</div>
            <div className="grid md:grid-cols-2 gap-2 max-h-96 overflow-y-auto">
              {notifs.map((n) => (
                <div key={n.id} className="border border-zinc-800 bg-zinc-900 rounded-sm px-4 py-2.5 text-xs flex justify-between items-center">
                  <span className="text-zinc-200">{n.message}</span>
                  <span className="text-zinc-600 font-mono text-[10px]">{n.created_at?.slice(11, 16)}</span>
                </div>
              ))}
              {notifs.length === 0 && <div className="text-xs text-zinc-500">Sin actividad reciente.</div>}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

const TONE_MAP = {
  accent: "border-[#FF5A00]/40 bg-[#FF5A00]/5 text-[#FF5A00]",
  teal: "border-[#0F766E]/40 bg-[#0F766E]/5 text-[#5EEAD4]",
  violet: "border-violet-500/40 bg-violet-500/5 text-violet-300",
  amber: "border-amber-500/40 bg-amber-500/5 text-amber-300",
  emerald: "border-emerald-500/40 bg-emerald-500/5 text-emerald-300",
  red: "border-red-500/40 bg-red-500/5 text-red-300",
  sky: "border-sky-500/40 bg-sky-500/5 text-sky-300",
};

const Stat = ({ label, value, accent, tone, testid }) => {
  let cls = "border-zinc-800 bg-zinc-900 text-white";
  let valCls = "text-white";
  const t = accent ? "accent" : tone;
  if (t && TONE_MAP[t]) {
    cls = TONE_MAP[t];
    valCls = TONE_MAP[t].split(" ").find(c => c.startsWith("text-")) || "text-white";
  }
  return (
    <Card data-testid={testid} className={`rounded-sm border ${cls}`}>
      <CardContent className="p-4">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
        <div className={`text-2xl font-semibold ${valCls}`}>{value ?? 0}</div>
      </CardContent>
    </Card>
  );
};
